import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TabBase } from '@core/base/tab.base';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvDynamicFormElement, DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, map, Observable, take, tap } from 'rxjs';
import { GetVehicleDataResp, VehicleServiceForm } from './interfaces';
import { VehicleServiceTabService } from './vehicle-service-tab.service';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { VehicleType } from '@shared/constants/vehicle-type';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';

@Component({
  templateUrl: './vehicle-service-tab.component.html',
  providers: [VehicleServiceTabService],
  styleUrls: ['./vehicle-service-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehicleServiceTabComponent
  extends TabBase
  implements PendingChanges, OnInit
{
  private agGridEntityService = inject(SplitterWithGridHelperService);
  private api = inject(RestApiService);
  tab = inject(VehicleServiceTabService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private translate = inject(TranslateService);
  private snackBar = inject(DvSnackbarService);
  private oidc = inject(OidcSecurityService);

  lastServiceForm: FormGroup<FormOf<Partial<VehicleServiceForm>>>;
  nextServiceForm: FormGroup<FormOf<Partial<VehicleServiceForm>>>;
  serviceElements: DvDynamicFormElement[];
  nextRoadWorthinessTestElements: DvDynamicFormElement[];
  selectedVehicleRegNumber: string;
  isVehicleDeleted?: boolean;

  private selectedVehicleId: string;
  private destroyRef = inject(DestroyRef);

  constructor() {
    super(inject(ActivatedRoute).parent!);
  }

  ngOnInit(): void {
    this.serviceElements = this.tab.getNextServiceElements();
    this.nextRoadWorthinessTestElements =
      this.tab.getMandatoryRoadWorthinessTestElements();

    this.createForms();
    this.subscribeToSelectedEntityId();
    this.subscribeToServicesMileageChanges();
  }

  hasChanges = (): boolean =>
    this.lastServiceForm.dirty && this.lastServiceForm.valid;
  pendingChangesSave = (): Observable<boolean> => this.performSave();

  onSave(): void {
    this.performSave().subscribe();
  }

  getLatestData(): void {
    this.api
      .getCustom<GetVehicleDataResp>(
        `${Entity.TrackerObject}/GetVehicleData/${this.entityId$.getValue()}`
      )
      .subscribe(resp => {
        this.tab.serviceEUInspectionNextDaysValue =
          resp.ServiceEUInspectionNextDays;
        this.updateNextRoadWorthinessTestColor();
        this.lastServiceForm.controls.ServiceEUInspectionLastDate?.setValue(
          resp.ServiceEUInspectionLastDate?.toString()
        );
        this.nextServiceForm.controls.ServiceEUInspectionNextDate?.setValue(
          resp.ServiceEUInspectionNextDate?.toString()
        );
        this.nextServiceForm.controls.ServiceEUInspectionNextDays?.setValue(
          resp.ServiceEUInspectionNextDays
        );
        this.agGridEntityService.refreshRowData(this.selectedVehicleId);
        this.snackBar.open(
          this.translate.instant('VehicleData.FetchingDataSucceeded'),
          SnackBarConfig.Success
        );
      });
  }

  private createForms(): void {
    this.lastServiceForm = DvFormUtils.createFormGroup([
      ...this.tab.serviceIntervalElements,
      ...this.tab.lastServiceElements,
      ...this.tab.serviceElements,
    ]);
    this.nextServiceForm = DvFormUtils.createFormGroup([
      ...this.serviceElements,
      ...this.nextRoadWorthinessTestElements,
    ]);
  }

  private updateNextRoadWorthinessTestColor(): void {
    this.nextRoadWorthinessTestElements =
      this.nextRoadWorthinessTestElements.map(e => {
        if (e.options.key === 'ServiceEUInspectionNextDays') {
          return {
            ...e,
            options: {
              ...e.options,
              backgroundColor: this.tab.setRoadWorthinessInputFieldColor(),
            },
          };
        } else {
          return e;
        }
      });
  }

  private subscribeToSelectedEntityId(): void {
    this.entityId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      if (!id) return;
      this.fetchAndSetFormValues(id);
    });
  }

  private fetchAndSetFormValues(id: string): void {
    const keys = Object.keys(this.lastServiceForm.controls);
    keys.push(
      'ServiceEUInspectionNextDate',
      'ServiceInMileage',
      'RegistrationNumber',
      'ServiceEUInspectionNextDays',
      'ShowRecord',
      'Username',
      'VehicleTypeDescription'
    );
    this.api
      .getInfo<VehicleServiceForm>(Entity.Vehicle, id, keys)
      .subscribe(data => {
        this.lastServiceForm.reset();

        const preparedData = this.tab.prepareDataForForms(data);
        this.selectedVehicleRegNumber = data.RegistrationNumber;
        this.selectedVehicleId = id;
        this.tab.serviceEUInspectionNextDaysValue = Number(
          data.ServiceEUInspectionNextDays
        );
        this.tab.nextServiceDaysValue = Number(data.NextServiceDays);
        this.updateNextRoadWorthinessTestColor();

        this.lastServiceForm.patchValue(preparedData);
        this.nextServiceForm.patchValue(preparedData as object);
        this.isVehicleDeleted = !data.ShowRecord;
        this.handleAccessRestrictions(
          !!data.ShowRecord,
          data.Username,
          data.VehicleTypeDescription
        );
      });
  }

  private performSave(): Observable<boolean> {
    const data = this.tab.prepareDataForSave(this.lastServiceForm.value);
    delete data.Mileage;
    const entityId = this.entityId$.value;
    return this.api.upsert(Entity.Vehicle, entityId, data).pipe(
      tap(() => {
        this.lastServiceForm.markAsPristine();
        this.agGridEntityService.refreshRowData(entityId);
        this.fetchAndSetFormValues(entityId);
      }),
      map(() => true)
    );
  }

  private handleAccessRestrictions(
    showRecord: boolean,
    defaultDriver: string | undefined,
    vehicleType: VehicleType | undefined
  ): void {
    combineLatest([
      this.permissionsService.hasPermissions([RolesAndPermissions.VehicleEdit]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.VehicleEditMyPrivate,
      ]),
      this.oidc.getUserData(),
    ])
      .pipe(take(1))
      .subscribe(([hasEdit, hasEditMyPrivate, { email }]) => {
        const isUsersPrivateVehicle =
          email.toLowerCase() === defaultDriver?.toLowerCase() &&
          vehicleType === VehicleType.Personal;

        if (
          (!hasEdit && !hasEditMyPrivate) ||
          (!hasEdit && hasEditMyPrivate && !isUsersPrivateVehicle) ||
          !showRecord
        ) {
          this.lastServiceForm.disable();
          this.nextServiceForm.disable();
        } else {
          this.lastServiceForm.enable();
        }
      });
  }

  private subscribeToServicesMileageChanges(): void {
    const lastServiceFormControls = this.lastServiceForm.controls;

    lastServiceFormControls.ServiceIntervalMileage?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.setServiceInMileageValue(
          val,
          lastServiceFormControls.ServiceLastMileage?.value,
          lastServiceFormControls.Mileage?.value
        );
      });

    lastServiceFormControls.ServiceLastMileage?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.setServiceInMileageValue(
          lastServiceFormControls.ServiceIntervalMileage?.value,
          val,
          lastServiceFormControls.Mileage?.value
        );
      });
  }

  private setServiceInMileageValue(
    serviceIntervalMileage?: number,
    serviceLastMileage?: number,
    mileage?: number
  ): void {
    const nextServiceFormControl =
      this.nextServiceForm.controls.ServiceInMileage;
    if (serviceLastMileage && serviceIntervalMileage) {
      nextServiceFormControl?.setValue(
        serviceLastMileage - (mileage ?? 0) + serviceIntervalMileage
      );
    } else {
      nextServiceFormControl?.setValue(undefined);
    }
  }
}
